import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';

import { type SwipeStatus } from '@services/AuthService';
import {
  type Referrals,
  type User,
  type VerificationStatus,
} from '@services/UsersService';

import { isStorableUser, reportRefusedUser } from '../utils';

interface AuthStore {
  user: User;
  verification_status: VerificationStatus;
  swipe_status: SwipeStatus;
  referrals: Referrals;
  /**
   * Stores `user`. Anything but an object (`undefined`, `null`, an array or a
   * primitive) is refused: the previous user stays, and the refusal is
   * reported once, with a stack that names the caller.
   */
  setUser: (user: User | null | undefined) => void;
  setVerificationStatus: (verificationStatus: VerificationStatus) => void;
  setReferrals: (referrals: Referrals) => void;
  setSwipeStatus: (swipeStatus: SwipeStatus) => void;
}

export const useAuthStore = create<AuthStore>()(
  persist(
    (set) => ({
      user: {} as User,
      verification_status: {} as VerificationStatus,
      swipe_status: {} as SwipeStatus,
      referrals: {} as Referrals,
      setUser: (user) => {
        if (!isStorableUser(user)) {
          reportRefusedUser('setUser', user);
          return;
        }

        set({ user });
      },
      setVerificationStatus: (verification_status) =>
        set({ verification_status }),
      setSwipeStatus: (swipe_status) => set({ swipe_status }),
      setReferrals: (referrals) => set({ referrals }),
    }),
    {
      name: 'auth-storage',
      storage: createJSONStorage(() => localStorage),
      // The default shallow merge, except that a persisted user that is not
      // an object (`null` survives JSON, `undefined` does not) falls back to
      // `{}`, as before `POST /auth` answers. The persisted state itself
      // passes the same check: a plain object.
      merge: (persistedState, currentState) => {
        const mergedState = {
          ...currentState,
          ...(isStorableUser(persistedState) ? persistedState : {}),
        };

        return {
          ...mergedState,
          user: isStorableUser(mergedState.user)
            ? mergedState.user
            : ({} as User),
        };
      },
    },
  ),
);
